import React, { Fragment, useEffect, useState, useMemo, useCallback } from 'react'
import { Dialog, Menu, Popover, Transition } from '@headlessui/react'
import {
  HiChevronDown,
  HiOutlineX,
  HiSearch,
  HiOutlineFilter,
  HiOutlineSortDescending
} from 'react-icons/hi'
import { InView } from 'react-intersection-observer'
import Meta from 'components/Meta'
import Layout from 'components/Layout'
import Alert from 'components/Alert'
import Filter from 'components/browse/Filter'
import MobileFilter from 'components/browse/MobileFilter'
import ActiveFilters from 'components/browse/ActiveFilters'
import PriceFilter from 'components/browse/PriceFilter'
import ForSaleToggle from 'components/browse/ForSaleToggle'
import ActivePriceFilter from 'components/browse/ActivePriceFilter'
import ActiveSearchFilter from 'components/browse/ActiveSearchFilter'
import Result from 'components/browse/Result'
import Loading from 'components/Loading'
import useBrowsePage from 'hooks/useBrowsePage'
import useDebounce from 'hooks/useDebounce'
import usePush from 'hooks/usePush'
import { useBrowseStore } from 'store/state/browseStore'
import { sortOptions, filters } from 'src/data/browse/filters'
import { classNames } from 'helpers/utilities'
import type { GetServerSideProps } from 'next'
import type { ParsedUrlQuery } from 'querystring'
import type { NfdBrowseParams, NfdBrowseSort, NfdRecordCollection } from 'api/api-client'

type BrowsePageProps = {
  pathname: string
  queryParams: ParsedUrlQuery | undefined
}

export default function BrowsePage({ pathname, queryParams }: BrowsePageProps) {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const [isSortDirty, setIsSortDirty] = useState(false)

  const category = useBrowseStore((state) => state.category)
  const setCategory = useBrowseStore((state) => state.setCategory)
  const length = useBrowseStore((state) => state.length)
  const setLength = useBrowseStore((state) => state.setLength)
  const state = useBrowseStore((state) => state.state)
  const setState = useBrowseStore((state) => state.setState)

  const { minPrice, maxPrice } = useBrowseStore((state) => state.priceRange)
  const setPriceRange = useBrowseStore((state) => state.setPriceRange)

  const sort = useBrowseStore((state) => state.sort)
  const setSort = useBrowseStore((state) => state.setSort)

  const name = useBrowseStore((state) => state.name)
  const setName = useBrowseStore((state) => state.setName)
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery, 500)

  useEffect(() => {
    const updatedQuery = debouncedSearchQuery.length > 0 ? debouncedSearchQuery : null
    setName(updatedQuery)
  }, [debouncedSearchQuery, setName])

  const clearAllFilters = useBrowseStore((state) => state.clearAllFilters)
  const resetDefault = useBrowseStore((state) => state.resetDefault)

  const query = useMemo(() => {
    const query: NfdBrowseParams = {}
    if (category) query.category = category
    if (length) query.length = length
    if (minPrice) query.minPrice = minPrice
    if (maxPrice) query.maxPrice = maxPrice
    if (sort) query.sort = sort
    if (state) query.state = state
    if (name) query.name = name
    return query
  }, [category, length, minPrice, maxPrice, sort, state, name])

  const {
    browseQuery: {
      data,
      error,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      isLoading,
      refetch
    },
    fetchedPages,
    setFetchedPages,
    getAuction,
    activeWalletAddress
  } = useBrowsePage(query)

  // if someone clicks Marketplace in main nav, show forSale/saleTypeAsc
  useEffect(() => {
    if (queryParams?.state === 'forSale') {
      window.scrollTo(0, 0)
      resetDefault()
    }
  }, [queryParams, resetDefault])

  const push = usePush()

  // are filters and sort set to forSale/saleTypeAsc?
  const isDefault = useMemo(() => {
    return (
      category === null &&
      length === null &&
      minPrice === null &&
      maxPrice === null &&
      sort === 'saleTypeAsc' &&
      name === null &&
      state?.length === 1 &&
      state?.includes('forSale')
    )
  }, [category, length, minPrice, maxPrice, sort, name, state])

  // if isDefault, add query param to URL, else remove
  useEffect(() => {
    isDefault
      ? push('/browse?state=forSale', '/browse?state=forSale', { shallow: true })
      : push('/browse', '/browse', { shallow: true })
  }, [isDefault, push])

  const hasActiveFilters = useMemo(() => {
    return (
      category !== null ||
      length !== null ||
      maxPrice !== null ||
      minPrice !== null ||
      // saleType !== null ||
      state !== null ||
      name !== null
    )
  }, [category, length, maxPrice, minPrice, state, name])

  const clearAll = useCallback(() => {
    window.scrollTo(0, 0)
    clearAllFilters()
    setSearchQuery('')

    !isSortDirty && setSort('createdDesc')
  }, [clearAllFilters, isSortDirty, setSort])

  const handleSortChange = (value: NfdBrowseSort) => {
    setIsSortDirty(true)
    setSort(value)
  }

  const [shouldFixMaxWidth, setShouldFixMaxWidth] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      const isXlScreen = window.matchMedia('(min-width: 1280px)').matches
      const numResults = data?.pages.reduce((acc, page) => acc + page.results.length, 0) || 0

      if (isXlScreen && numResults > 0) {
        const sidebarWidth = 256
        const mainWidth = window.innerWidth - sidebarWidth
        const paddingX = 64 // pl + pr
        const gridGapX = 32
        const maxItemWidth = 280

        const availableSpace = mainWidth - paddingX - gridGapX * (numResults - 1)
        const shouldSetFixedMaxWidth = availableSpace / numResults > maxItemWidth

        setShouldFixMaxWidth(shouldSetFixedMaxWidth)
      }
    }

    handleResize()

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [data?.pages])

  const gridClasses = classNames(
    shouldFixMaxWidth
      ? 'xl:grid-cols-[repeat(auto-fit,_minmax(260px,_280px))]'
      : 'xl:grid-cols-[repeat(auto-fit,_minmax(260px,_1fr))]',
    'space-y-4 md:space-y-0 md:grid md:grid-cols-3 md:gap-y-10 md:gap-x-6 xl:gap-x-8'
  )

  const refetchPage = (pageIdx: number) => {
    refetch({ refetchPage: (page, index) => index === pageIdx })
    setFetchedPages((prev) => new Set(prev.add(pageIdx)))
  }

  const renderResultPage = (results: NfdRecordCollection, pageIdx: number) => {
    if (!data) {
      return null
    }

    // @todo: find a way to filter dupes w/o breaking infinite scroll

    // const prevPages = data.pages.slice(0, pageIdx)
    // const prevResultNames =
    //   prevPages.flatMap((page) => page.results).flatMap((result) => result.name) || []
    // const filteredResults = results.filter((result) => !prevResultNames.includes(result.name))

    return results.map((nfd, i) => {
      if (i === results.length - 1) {
        // last element on the page

        if (pageIdx === data.pages.length - 1 && hasNextPage) {
          // this is the last page and there are more pages to fetch

          return (
            <InView key={nfd.name} onChange={(inView) => inView && fetchNextPage()}>
              {({ ref }) => (
                <div ref={ref}>
                  <Result
                    nfd={nfd}
                    activeWallet={activeWalletAddress}
                    auction={getAuction(nfd.name)}
                  />
                </div>
              )}
            </InView>
          )
        }
      }

      if (i === 0) {
        // first element on the page

        if (!fetchedPages.has(pageIdx)) {
          // after navigating back, this page is not fetched yet

          return (
            <InView key={nfd.name} onChange={(inView) => inView && refetchPage(pageIdx)}>
              {({ ref }) => (
                <div ref={ref}>
                  <Result
                    nfd={nfd}
                    activeWallet={activeWalletAddress}
                    auction={getAuction(nfd.name)}
                  />
                </div>
              )}
            </InView>
          )
        }
      }

      return (
        <Result
          key={nfd.name}
          nfd={nfd}
          activeWallet={activeWalletAddress}
          auction={getAuction(nfd.name)}
        />
      )
    })
  }

  const renderResults = () => {
    if (isLoading && !isFetchingNextPage) {
      // @todo: show skeletons for results
      return (
        <div className="flex flex-col items-center justify-center py-32">
          <Loading />
        </div>
      )
    }

    if (error) {
      return <Alert type="error" title={`Error fetching search results`} error={error} />
    }

    if (!data?.pages[0]?.results?.length) {
      return <Alert type="info" title="No results found" />
    }

    return (
      <div className={gridClasses}>
        {data.pages.map((page, pageIdx) => (
          <Fragment key={pageIdx}>{renderResultPage(page.results, pageIdx)}</Fragment>
        ))}
      </div>
    )
  }

  const renderSort = () => {
    const selectedSort = sortOptions.find((option) => option.value === sort)
    const sortLabel = selectedSort ? (
      <span className="inline-flex items-center space-x-2 md:hidden xl:inline-flex">
        <HiOutlineSortDescending className="h-5 w-5 text-gray-600" aria-hidden="true" />{' '}
        <span className="sr-only">Sort by </span>
        <strong className="font-semibold">{selectedSort.label}</strong>
      </span>
    ) : (
      <span className="md:hidden xl:inline-block">Sort</span>
    )

    return (
      <Menu as="div" className="relative inline-block text-left">
        <div className="flex items-center">
          <Menu.Button className="group inline-flex justify-center text-sm font-medium text-gray-700 hover:text-gray-900 whitespace-nowrap outline-brand-500 mr-3">
            {sortLabel}
            <span className="items-center space-x-2 hidden md:inline-flex xl:hidden">
              <HiOutlineSortDescending className="h-5 w-5 text-gray-600" aria-hidden="true" />{' '}
              <strong className="font-semibold">Sort</strong>
            </span>
            <HiChevronDown
              className="flex-shrink-0 mr-1 ml-1 h-5 w-5 text-gray-400 group-hover:text-gray-500"
              aria-hidden="true"
            />
          </Menu.Button>
        </div>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="origin-top-left absolute left-0 mt-2 w-40 rounded-md shadow-2xl bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
            <div className="py-1">
              {sortOptions.map((option) => (
                <Menu.Item key={option.label}>
                  {({ active }) => (
                    <button
                      type="button"
                      className={classNames(
                        sort === option.value ? 'font-semibold' : '',
                        active ? 'bg-gray-100 ' : '',
                        'w-full text-left block px-4 py-2 text-sm text-gray-900'
                      )}
                      onClick={() => handleSortChange(option.value)}
                    >
                      {option.label}
                    </button>
                  )}
                </Menu.Item>
              ))}
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    )
  }

  const renderFilters = (type: 'mobile' | 'desktop') => {
    const FilterComponent = type === 'mobile' ? MobileFilter : Filter

    return (
      <>
        <FilterComponent
          queryKey="category"
          name={filters.category.label}
          options={filters.category.options}
          onChange={setCategory}
          value={category}
        />
        {/* <FilterComponent
          queryKey="state"
          name={filters.state.label}
          options={filters.state.options}
          onChange={setState}
          value={state}
        /> */}
        <FilterComponent
          queryKey="length"
          name={filters.length.label}
          options={filters.length.options}
          onChange={setLength}
          value={length}
        />
        <PriceFilter
          type={type}
          minPrice={minPrice}
          maxPrice={maxPrice}
          setPriceRange={setPriceRange}
        />
      </>
    )
  }

  return (
    <>
      <Meta title="Marketplace" pathname={pathname} />
      <Layout pathname={pathname}>
        {/* Mobile filter dialog */}
        <Transition.Root show={mobileFiltersOpen} as={Fragment}>
          <Dialog
            as="div"
            className="fixed inset-0 flex z-50 md:hidden"
            onClose={setMobileFiltersOpen}
          >
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="ml-auto relative max-w-xs w-full h-full bg-white shadow-xl py-4 pb-12 flex flex-col overflow-y-auto">
                <div className="px-4 flex items-center justify-between">
                  <h2 className="text-lg font-medium text-gray-900">Filters</h2>
                  <button
                    type="button"
                    className="-mr-2 w-10 h-10 bg-white p-2 rounded-md flex items-center justify-center text-gray-400 outline-brand-500"
                    onClick={() => setMobileFiltersOpen(false)}
                  >
                    <span className="sr-only">Close menu</span>
                    <HiOutlineX className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="px-4 pt-4 mt-4 border-t border-gray-200">
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <HiSearch className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </div>
                    <input
                      type="search"
                      name="searchQueryMobile"
                      id="searchQueryMobile"
                      className="focus:ring-brand-500 focus:border-brand-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                      placeholder="Filter by search text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>

                <div className="px-4 pt-4 mt-4 border-t border-gray-200">
                  <ForSaleToggle state={state} onChange={setState} className="my-1" />
                </div>

                {/* Mobile filters */}
                <form className="mt-4">{renderFilters('mobile')}</form>
              </div>
            </Transition.Child>
          </Dialog>
        </Transition.Root>

        <main>
          <div className="bg-white">
            <div className="pt-8 pb-4 sm:pt-12 md:pt-16 px-4 sm:px-6 lg:px-8">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900">
                Marketplace
              </h1>
            </div>
          </div>

          {/* Filters */}
          <section aria-labelledby="filter-heading" className="sticky top-16 z-30">
            <h2 id="filter-heading" className="sr-only">
              Filters
            </h2>

            <div className="relative z-30 bg-white border-b border-gray-200 py-4 md:py-3">
              <div className="px-4 flex items-center justify-between sm:px-6 lg:px-8">
                {renderSort()}

                <div className="flex items-center">
                  <button
                    type="button"
                    className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 md:hidden group"
                    onClick={() => setMobileFiltersOpen(true)}
                  >
                    <HiOutlineFilter
                      className="-ml-0.5 mr-2 h-4 w-4 text-gray-400 group-hover:text-gray-500"
                      aria-hidden="true"
                    />
                    Filters
                  </button>

                  <div className="hidden md:block mr-5">
                    <div className="relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <HiSearch className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </div>
                      <input
                        type="text"
                        name="searchQuery"
                        id="searchQuery"
                        className="focus:ring-brand-500 focus:border-brand-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                        placeholder="Filter by search text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="hidden md:block mr-5">
                    <ForSaleToggle state={state} onChange={setState} />
                  </div>

                  <div className="hidden md:block">
                    <div className="flow-root">
                      <Popover.Group className="-mr-4 flex items-center divide-x divide-gray-200 border-l border-gray-200">
                        {renderFilters('desktop')}
                      </Popover.Group>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Active filters */}
            <div className="bg-gray-100">
              <div className="py-3 px-4 sm:flex sm:items-center sm:px-6 lg:px-8">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 py-2 border border-transparent">
                  Filters
                  <span className="sr-only">, active</span>
                </h3>

                <div aria-hidden="true" className="hidden w-px h-5 bg-gray-300 sm:block sm:ml-4" />

                <div className="mt-2 sm:mt-0 sm:ml-4">
                  <div className="-m-1 flex flex-wrap items-center">
                    <ActiveSearchFilter name={name} setName={setName} />
                    <ActiveFilters
                      options={filters.category.options}
                      onChange={setCategory}
                      value={category}
                    />
                    <ActiveFilters
                      options={filters.state.options}
                      onChange={setState}
                      value={state}
                    />
                    <ActiveFilters
                      options={filters.length.options}
                      onChange={setLength}
                      value={length}
                    />
                    {/* <ActiveFilters
                      options={filters.saleType.options}
                      onChange={setSaleType}
                      value={saleType}
                    /> */}
                    {/* <ActiveFilters
                      options={filters.traits.options}
                      onChange={setTraits}
                      value={traits}
                    /> */}
                    <ActivePriceFilter
                      minPrice={minPrice}
                      maxPrice={maxPrice}
                      setPriceRange={setPriceRange}
                    />

                    {hasActiveFilters && (
                      <button
                        type="button"
                        className="rounded-full items-center py-2 px-3 text-xs font-semibold uppercase tracking-wide mx-2 text-gray-700 hover:text-gray-900 outline-brand-500"
                        onClick={clearAll}
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Results */}
          <section
            aria-labelledby="results-heading"
            className="pt-8 pb-16 px-4 sm:pt-12 sm:pb-24 sm:px-6 lg:px-8"
          >
            <h2 id="results-heading" className="sr-only">
              Results
            </h2>

            {renderResults()}

            {isFetchingNextPage && (
              <div className="flex flex-col items-center justify-center py-32">
                <Loading />
              </div>
            )}
          </section>
        </main>
      </Layout>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  return {
    props: {
      pathname: context.resolvedUrl,
      queryParams: context.query
    }
  }
}
